import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Organization Billing - Plan and billing management
 *
 * Pricing:
 * - Team Plan: $29/month base (includes 3 seats)
 * - Additional seats: $10/seat/month
 *
 * Features:
 * - Current plan display
 * - Seat usage and management
 * - Add seats functionality
 * - Billing history
 */

const BASE_PLAN_PRICE = 29;
const BASE_SEATS_INCLUDED = 3;
const PRICE_PER_EXTRA_SEAT = 10;

export default function OrgBilling({ organization, members, stats }) {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [additionalSeats, setAdditionalSeats] = useState(1);
  const [saving, setSaving] = useState(false);

  const seatsUsed = members?.filter(m => m.status === 'active').length || 0;
  const totalSeats = organization?.seats || BASE_SEATS_INCLUDED;
  const seatPercentage = Math.min((seatsUsed / totalSeats) * 100, 100);

  // Calculate current billing
  const extraSeats = Math.max(0, totalSeats - BASE_SEATS_INCLUDED);
  const extraSeatsCost = extraSeats * PRICE_PER_EXTRA_SEAT;
  const currentMonthlyTotal = BASE_PLAN_PRICE + extraSeatsCost;

  // Calculate new billing after adding seats
  const newTotalSeats = totalSeats + additionalSeats;
  const newExtraSeats = Math.max(0, newTotalSeats - BASE_SEATS_INCLUDED);
  const newExtraSeatsCost = newExtraSeats * PRICE_PER_EXTRA_SEAT;
  const newMonthlyTotal = BASE_PLAN_PRICE + newExtraSeatsCost;
  const additionalCost = additionalSeats * PRICE_PER_EXTRA_SEAT;

  const handleAddSeats = async () => {
    if (additionalSeats < 1) return;

    try {
      setSaving(true);
      const orgRef = doc(db, 'organizations', organization.id);
      await updateDoc(orgRef, {
        seats: newTotalSeats
      });
      setShowUpgradeModal(false);
      setAdditionalSeats(1);
    } catch (error) {
      console.error('Error adding seats:', error);
      alert('Failed to add seats. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Generate billing history based on current plan
  const generateBillingHistory = () => {
    const history = [];
    const now = new Date();

    for (let i = 0; i < 3; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

      history.push({
        id: i + 1,
        date: monthName,
        amount: `$${currentMonthlyTotal.toFixed(2)}`,
        status: 'Paid',
        description: extraSeats > 0
          ? `Team Plan + ${extraSeats} extra seat${extraSeats > 1 ? 's' : ''}`
          : 'Team Plan - Monthly'
      });
    }

    return history;
  };

  const billingHistory = generateBillingHistory();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Billing & Plan</h2>
        <p className="text-white/50 text-sm mt-1">Manage your subscription and billing</p>
      </div>

      {/* Current Plan */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-white font-semibold text-lg">Team Plan</h3>
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-purple-500/30 to-blue-500/30 text-purple-300 border border-purple-500/30">
                Active
              </span>
            </div>
            <p className="text-white/50 text-sm mt-1">Perfect for growing teams</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-white">${currentMonthlyTotal}</div>
            <div className="text-white/50 text-sm">/month</div>
          </div>
        </div>

        {/* Pricing Breakdown */}
        <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-white/70">Base plan ({BASE_SEATS_INCLUDED} seats included)</span>
            <span className="text-white">${BASE_PLAN_PRICE}/mo</span>
          </div>
          {extraSeats > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Extra seats ({extraSeats} × ${PRICE_PER_EXTRA_SEAT})</span>
              <span className="text-white">${extraSeatsCost}/mo</span>
            </div>
          )}
          <div className="flex justify-between text-sm pt-2 border-t border-white/10">
            <span className="text-white font-medium">Total</span>
            <span className="text-white font-bold">${currentMonthlyTotal}/mo</span>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/10">
          <div>
            <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Total Seats</div>
            <div className="text-white font-semibold">{totalSeats} seats</div>
          </div>
          <div>
            <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Storage</div>
            <div className="text-white font-semibold">{stats?.storageLimit || 1000} GB</div>
          </div>
          <div>
            <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Recordings</div>
            <div className="text-white font-semibold">Unlimited</div>
          </div>
          <div>
            <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Support</div>
            <div className="text-white font-semibold">Priority</div>
          </div>
        </div>
      </div>

      {/* Seat Usage */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold">Seat Usage</h3>
            <p className="text-white/50 text-xs mt-0.5">
              {BASE_SEATS_INCLUDED} seats included, +${PRICE_PER_EXTRA_SEAT}/seat for additional
            </p>
          </div>
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-[#06c] to-purple-600 text-white text-sm font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all"
          >
            + Add Seats
          </button>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="h-3 bg-white/10 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  seatPercentage > 90 ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                  seatPercentage > 70 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                  'bg-gradient-to-r from-[#06c] to-purple-500'
                }`}
                style={{ width: `${seatPercentage}%` }}
              />
            </div>
          </div>
          <span className="text-white font-medium">{seatsUsed} / {totalSeats}</span>
        </div>

        {seatPercentage > 80 && (
          <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
            <p className="text-yellow-300 text-sm">
              You're running low on seats. Add more seats at just ${PRICE_PER_EXTRA_SEAT}/seat/month.
            </p>
          </div>
        )}

        {seatsUsed >= totalSeats && (
          <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
            <p className="text-red-300 text-sm">
              All seats are in use. Add more seats to invite new members.
            </p>
          </div>
        )}
      </div>

      {/* Payment Method */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Payment Method</h3>
          <button className="text-[#06c] text-sm font-medium hover:text-blue-400 transition-colors">
            Update
          </button>
        </div>

        <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="w-12 h-8 rounded bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center">
            <span className="text-white text-xs font-bold">VISA</span>
          </div>
          <div className="flex-1">
            <div className="text-white font-medium">•••• •••• •••• 4242</div>
            <div className="text-white/50 text-xs">Expires 12/25</div>
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-white font-semibold">Billing History</h3>
        </div>

        <div className="divide-y divide-white/5">
          {billingHistory.map(item => (
            <div key={item.id} className="flex items-center justify-between px-6 py-4">
              <div>
                <div className="text-white font-medium text-sm">{item.description}</div>
                <div className="text-white/50 text-xs mt-0.5">{item.date}</div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-white font-medium">{item.amount}</span>
                <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">
                  {item.status}
                </span>
                <button className="text-[#06c] text-xs hover:text-blue-400">
                  Invoice
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Seats Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-[#1c1c1e] rounded-2xl border border-white/10 max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-lg mb-2">Add More Seats</h3>
            <p className="text-white/60 text-sm mb-6">
              Each additional seat costs <span className="text-white font-medium">${PRICE_PER_EXTRA_SEAT}/month</span>. Your billing will be prorated.
            </p>

            {/* Seat Counter */}
            <div className="flex items-center justify-center gap-6 mb-6">
              <button
                onClick={() => setAdditionalSeats(Math.max(1, additionalSeats - 1))}
                disabled={additionalSeats <= 1}
                className="w-12 h-12 rounded-xl bg-white/10 text-white text-xl font-medium hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                −
              </button>
              <div className="text-center min-w-[100px]">
                <div className="text-4xl font-bold text-white">{additionalSeats}</div>
                <div className="text-white/50 text-xs mt-1">seat{additionalSeats > 1 ? 's' : ''} to add</div>
              </div>
              <button
                onClick={() => setAdditionalSeats(additionalSeats + 1)}
                className="w-12 h-12 rounded-xl bg-white/10 text-white text-xl font-medium hover:bg-white/20 transition-colors"
              >
                +
              </button>
            </div>

            {/* Price Breakdown */}
            <div className="p-4 rounded-xl bg-white/5 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Current seats</span>
                <span className="text-white">{totalSeats}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Adding</span>
                <span className="text-green-400">+{additionalSeats}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                <span className="text-white/60">New total</span>
                <span className="text-white font-medium">{newTotalSeats} seats</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                <span className="text-white/60">Additional cost</span>
                <span className="text-white font-medium">+${additionalCost}/mo</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white font-medium">New monthly total</span>
                <span className="text-white font-bold">${newMonthlyTotal}/mo</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                disabled={saving}
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSeats}
                disabled={saving}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-[#06c] to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50"
              >
                {saving ? 'Adding...' : `Add ${additionalSeats} Seat${additionalSeats > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
